import { useContext } from 'react'
import { bool, number, object } from 'prop-types'

import localeContext from '../../../contexts/Locale'
import useTranslate from '../../../hooks/useTranslate'

import {
  buildOrbitBIBPendingDataList,
  buildOrbitRecurringCreditsList,
  getMonthlyTotalPriceOfTerm,
  getPendingBalanceFromOffer
} from '../../../utils/mobility'
import ItemsPrice from '../../molecules/CartItem/PriceBreakdown/ItemsPrice'
import PriceBreakdownContainer from '../../molecules/CartItem/PriceBreakdown/PriceBreakdownContainer'
import {
  buildCreditsAndDiscountsList,
  buildOtherChargesList,
  buildPlanPrice,
  buildRecurringDiscountList,
  buildWarrantyInfoPrice,
  calculateDueTodaySubTotal,
  getOneTimeEasyPaymentPriceBreakdown
} from './utils'
import NameAndPrice from '../../atoms/ItemDetails/NameAndPrice'
import HeadingWithSubItems from '../../molecules/CartItem/PriceBreakdown/HeadingWithSubItems'
import FinalPrice from '../../molecules/CartItem/PriceBreakdown/FinalPrice'
import { OtherChargesAndCreditsContainer } from '../../molecules/CartItem/PriceBreakdown'
import {
  buildMobilityDeviceOneTimeDiscountList,
  buildMobilityMonthlyEasyPaymentPrice,
  buildOrbitBIBAmount,
  buildOrbitPendingBalancesList,
  buildVasAddOnsList
} from '../../../utils/display'

const OfferDetails = ({ connectedDeviceOffer, connectedDevice, plan, term, isOrbit }) => {
  const { lang } = useContext(localeContext)
  const translate = useTranslate()

  const dueTodaySubTotal = calculateDueTodaySubTotal(connectedDeviceOffer)
  const { subTotal: monthlySubTotal } = getMonthlyTotalPriceOfTerm(term, connectedDeviceOffer)

  const pendingBalancesFromOffer = getPendingBalanceFromOffer(connectedDeviceOffer)
  const hasOrbitPendingBalancePenalty = pendingBalancesFromOffer?.hasPenalty
  const orbitPendingFinalAmount = pendingBalancesFromOffer?.total
  const orbitPendingBalancesData = buildOrbitPendingBalancesList(
    connectedDeviceOffer,
    lang,
    translate
  )
  const orbitBIBAmountData = buildOrbitBIBAmount(connectedDevice)

  const oneTimeEasyPaymentData = getOneTimeEasyPaymentPriceBreakdown(
    plan,
    connectedDevice,
    translate,
    lang
  )
  const monthlyEasyPaymentPriceData = buildMobilityMonthlyEasyPaymentPrice({
    plan,
    device: connectedDevice,
    translate,
    lang
  })
  const planPriceData = buildPlanPrice(plan, lang)
  const recurringDiscountsData = buildRecurringDiscountList(connectedDevice, plan, lang)
  const monthlyWarrantyPriceData = buildWarrantyInfoPrice(connectedDeviceOffer, lang)
  const vasAddonsData = buildVasAddOnsList({ offer: connectedDeviceOffer, plan, translate, lang })

  const otherChargesData = buildOtherChargesList(connectedDeviceOffer, translate, lang)
  const otherCreditsAndDiscountsData = buildCreditsAndDiscountsList(
    connectedDeviceOffer,
    plan,
    connectedDevice,
    lang,
    isOrbit
  )

  const orbitBIBPendingData = buildOrbitBIBPendingDataList(connectedDeviceOffer, lang, translate)
  const orbitRecurringData = buildOrbitRecurringCreditsList(connectedDeviceOffer, translate, lang)
  const pendingAmountsData = [...orbitBIBPendingData, ...orbitRecurringData]

  const orbitPendingBalances = orbitPendingBalancesData?.map((balance) => (
    <NameAndPrice
      key={balance.id}
      id={balance.id}
      name={balance.name}
      price={balance.price}
      shouldMaskPrice
    />
  ))

  const orbitBIBAmount = orbitBIBAmountData && (
    <NameAndPrice
      id={orbitBIBAmountData.id}
      name={orbitBIBAmountData.name}
      price={orbitBIBAmountData.price}
      prefixIcon={orbitBIBAmountData.prefixIcon}
    />
  )

  const oneTimeEasyPaymentPrice = oneTimeEasyPaymentData && (
    <NameAndPrice
      id={oneTimeEasyPaymentData.id}
      name={oneTimeEasyPaymentData.name}
      price={oneTimeEasyPaymentData.price}
    />
  )

  const oneTimeDeviceDiscounts = buildMobilityDeviceOneTimeDiscountList(connectedDevice, lang)?.map(
    (discount) => (
      <NameAndPrice
        id={discount.id}
        key={discount.id}
        name={discount.name}
        price={discount.price}
        prefixIcon={discount.prefixIcon}
      />
    )
  )

  const monthlyEasyPaymentPrice = monthlyEasyPaymentPriceData && (
    <NameAndPrice
      id={monthlyEasyPaymentPriceData.id}
      name={monthlyEasyPaymentPriceData.name}
      price={monthlyEasyPaymentPriceData.price}
    />
  )

  const planPrice = <NameAndPrice price={planPriceData.price} name={planPriceData.name} />

  const recurringDiscounts = recurringDiscountsData?.map((discount) => (
    <NameAndPrice
      id={discount.id}
      key={discount.id}
      name={discount.name}
      price={discount.price}
      prefixIcon={discount.prefixIcon}
    />
  ))

  const monthlyWarrantyPrice = monthlyWarrantyPriceData && (
    <NameAndPrice
      id={monthlyWarrantyPriceData.id}
      name={monthlyWarrantyPriceData.name}
      price={monthlyWarrantyPriceData.price}
    />
  )

  const vasAddons = (
    <HeadingWithSubItems subItems={vasAddonsData} headingText={translate('addons')} />
  )

  const otherCharges = (
    <HeadingWithSubItems
      subItems={otherChargesData}
      headingText={translate('additionalChargesAndCredits.additionalCharges')}
    />
  )

  const otherCreditsAndDiscounts = (
    <HeadingWithSubItems
      subItems={otherCreditsAndDiscountsData}
      headingText={translate('additionalChargesAndCredits.additionalCreditsAndDiscounts')}
    />
  )

  const orbitPendingAmounts = (
    <HeadingWithSubItems
      subItems={pendingAmountsData}
      headingText={translate('additionalChargesAndCredits.pendingAmounts')}
    />
  )

  return (
    <>
      <PriceBreakdownContainer>
        {orbitPendingBalances}
        {orbitPendingFinalAmount > 0 ||
          (hasOrbitPendingBalancePenalty && (
            <FinalPrice
              price={orbitPendingFinalAmount}
              overrideLabelPath="orbit.currentDeviceBalance"
              shouldMaskPrice
            />
          ))}
        <ItemsPrice
          items={[connectedDevice]}
          useDisplayNameOverride={translate('priceBreakdown.retailPrice')}
        />
        {orbitBIBAmount}
        {oneTimeEasyPaymentPrice}
        {oneTimeDeviceDiscounts}
        <FinalPrice price={dueTodaySubTotal} />
        {monthlyEasyPaymentPrice}
        {planPrice}
        {recurringDiscounts}
        {monthlyWarrantyPrice}
        {vasAddons}
        <FinalPrice price={monthlySubTotal} isMonthly />
      </PriceBreakdownContainer>
      <OtherChargesAndCreditsContainer
        listData={[...otherChargesData, ...otherCreditsAndDiscountsData, ...pendingAmountsData]}
      >
        {otherCharges}
        {otherCreditsAndDiscounts}
        {orbitPendingAmounts}
      </OtherChargesAndCreditsContainer>
    </>
  )
}

export default OfferDetails

OfferDetails.propTypes = {
  connectedDeviceOffer: object.isRequired,
  connectedDevice: object.isRequired,
  plan: object.isRequired,
  term: number.isRequired,
  isOrbit: bool
}

OfferDetails.defaultProps = {
  isOrbit: false
}
